import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  useColorModeValue,
  useToast,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  VStack,
  HStack,
  IconButton,
  Tooltip,
  Icon,
  Center,
  Skeleton,
  useBreakpoint,
} from '@chakra-ui/react';
import styles from './styles.module.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { WalletPortfolio } from '../../components/WalletPortfolio';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { ShareIcon } from '../../components/icons/ShareIcon';
import { Header } from '../../components/Header';
import { WalletData, Token } from '../../types/wallet';
import { copyToClipboard } from '../../utils/clipboard';
import { useWallet } from '../../contexts/WalletContext';
import { getSolBalance, getWalletBalances, getTokenPrice } from '../../utils/getBalances';
import { createRawTransaction } from '../../utils/createSwapTransaction';
import { Transaction, PublicKey, VersionedTransaction } from '@solana/web3.js';
import { CopyProgressModal } from '../../components/CopyProgressModal';
import { sendConfirmTransaction } from '../../utils/sendConfirmTransaction';
import { TransactionDetail } from '../../components/CopyProgressModal/CopyProgressModal';
import { WarningTokenModal } from '../../components/WarningTokenModal/WarningTokenModal';
import { Typography } from '@/kit/antipad-ui/Typography';
import { Button as ButtonKit } from '@/kit/antipad-ui/Button';
import { ToastComponent } from '@/components/ToastComponent';
import { useSnackbar } from '@/hooks/useSnackbar';
import { spacing } from '@/kit/antipad-ui';
interface PhantomWindow extends Window {
  solana?: {
    isPhantom?: boolean;
    isConnected: boolean;
    publicKey: PublicKey | null;
    connect: (options?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey: PublicKey }>;
    disconnect: () => Promise<void>;
    on: (event: string, callback: (args: any) => void) => void;
    removeListener: (event: string, callback: (args: any) => void) => void;
    signAndSendTransaction: (transaction: Transaction | VersionedTransaction) => Promise<{ signature: string }>;
    signAllTransactions: (transactions: VersionedTransaction[]) => Promise<VersionedTransaction[]>;
  };
}

declare const window: PhantomWindow;

interface TransactionStatus {
  text: string;
  status: 'pending' | 'success' | 'error' | 'warning';
  errorMessage?: string;
}

export const Portfolio: React.FC = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopying, setIsCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { message } = useSnackbar();
  const { publicKey } = useWallet();
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [solUsdBalance, setSolUsdBalance] = useState<number | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyProgress, setCopyProgress] = useState({
    currentStep: 0,
    totalSteps: 0,
    currentStatus: '',
    progress: 0,
    details: [] as TransactionStatus[],
    isCompleted: false,
    isError: false,
    errorMessage: '',
    statusMessage: '',
    statusType: 'pending' as 'pending' | 'success' | 'warning' | 'error',
    successStats: {
      totalTokens: 0,
      totalAmount: 0,
      successfulTransactions: 0,
      failedTransactions: 0
    },
    currentTransaction: null as TransactionDetail | null
  });
  const screen = useBreakpoint();
  const isMobile = screen === 'base' || screen === 'sm';
  const [warningModalState, setWarningModalState] = useState<{
    isOpen: boolean;
    token: Token | null;
    onConfirm: () => void;
  }>({
    isOpen: false,
    token: null,
    onConfirm: () => { },
  });

  const address = searchParams.get('address');

  useEffect(() => {
    const loadWalletData = async () => {
      setIsLoading(true);

      if (!address) {
        setError('No wallet address provided');
        setIsLoading(false);
        return;
      }

      if (!address.match(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/)) {
        setError('Invalid Solana address format');
        setIsLoading(false);
        return;
      }

      try {
        const solBalanceData = await getSolBalance(address);
        setSolBalance(solBalanceData);

        const walletData = await getWalletBalances(address);
        console.log('Wallet data received:', walletData);

        const tokensWithSelection = walletData.tokens.map((token: Token) => {
          console.log('Processing token in Portfolio:', token);
          return {
            ...token,
            selected: token.hasPool,
            logoUrl: token.icon || `https://cryptologos.cc/logos/${token.name.toLowerCase().replace(' ', '-')}-${token.symbol.toLowerCase()}-logo.png?v=025`
          };
        });

        console.log('Tokens with selection:', tokensWithSelection);

        setWallet({
          address: walletData.address,
          balance: walletData.balance,
          tokens: tokensWithSelection
        });
        setError(null);

        const prices = await getTokenPrice("solana");
        const solPrice = prices.find((price: { symbol: string; price: number }) => price.symbol === "solana")?.price || 0;
        setSolUsdBalance(solBalanceData ? solBalanceData * solPrice : 0);
      } catch (err) {
        setError('Failed to load wallet data');
        setWallet(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadWalletData();
  }, [address]);

  const handleCopyAddress = () => {
    if (wallet?.address) {
      copyToClipboard(wallet.address);
      message('Address copied to clipboard', 'success');
    }
  };

  const sendTransactions = async (transactions: VersionedTransaction[]) => {
    try {
      const { solana } = window;
      if (!solana) {
        throw new Error('Phantom wallet not found');
      }

      if (!transactions.length) {
        throw new Error('No transactions to sign');
      }

      const signedTransactions = await solana.signAllTransactions(transactions);

      const base64Transactions = signedTransactions.map(tx => {
        const rawTx = tx.serialize();
        return window.btoa(Array.from(rawTx).map(byte => String.fromCharCode(byte)).join(''));
      });

      const result = await sendConfirmTransaction(base64Transactions);
      return result;
    } catch (error) {
      console.error('Error signing and sending transactions:', error);
      throw error;
    }
  };

  const handleCopyPortfolio = async (
    calculatedAmounts: Record<string, { usd: number; tokens: number }>,
    purchaseStrategy: 'full' | 'proportional' | 'equal',
    investmentAmount: number,
    selectedTokens: Token[]
  ) => {
    if (!wallet || !publicKey) {
      message('Please connect your wallet', 'error');
      return;
    }

    setIsCopying(true);
    setIsCopyModalOpen(true);
    setCopyProgress({
      currentStep: 0,
      totalSteps: selectedTokens.length + 1,
      currentStatus: 'Preparing transactions...',
      progress: 0,
      details: [],
      isCompleted: false,
      isError: false,
      errorMessage: '',
      statusMessage: '',
      statusType: 'pending',
      successStats: {
        totalTokens: selectedTokens.length,
        totalAmount: 0,
        successfulTransactions: 0,
        failedTransactions: 0
      },
      currentTransaction: null
    });

    try {
      const prices = await getTokenPrice("solana");
      const solPrice = prices.find((price: { symbol: string; price: number }) => price.symbol === "solana")?.price || 0;

      if (solPrice === 0) {
        throw new Error('Failed to get SOL price');
      }

      console.log('Selected tokens for transaction:', selectedTokens.map(t => t.symbol));

      const transactions: VersionedTransaction[] = [];
      let totalAmount = 0;
      let successfulTransactions = 0;
      let failedTransactions = 0;

      for (const token of selectedTokens) {
        setCopyProgress(prev => ({
          ...prev,
          currentStep: prev.currentStep + 1,
          currentStatus: `Creating transaction for ${token.symbol}...`,
          details: [...prev.details, {
            text: `Preparing transaction for ${token.symbol}`,
            status: 'pending' as const
          }],
          currentTransaction: {
            symbol: token.symbol,
            icon: token.icon || `https://cryptologos.cc/logos/${token.symbol.toLowerCase()}-${token.symbol.toLowerCase()}-logo.png`,
            amount: calculatedAmounts[token.symbol]?.tokens || token.amount,
            usdValue: calculatedAmounts[token.symbol]?.usd || token.usdValue,
            status: 'pending'
          }
        }));

        console.log('Processing token:', {
          symbol: token.symbol,
          mint: token.mint,
          selected: token.selected
        });
        const calculatedAmount = calculatedAmounts[token.symbol];
        let amountInUsd = 0;

        switch (purchaseStrategy) {
          case 'full':
            amountInUsd = token.usdValue;
            break;
          case 'proportional':
            const proportionalAmount = calculatedAmount?.usd || 0;
            if (proportionalAmount < 0.1) {
              console.warn('Proportional amount too small:', {
                tokenSymbol: token.symbol,
                amount: proportionalAmount
              });
              setCopyProgress(prev => ({
                ...prev,
                currentTransaction: {
                  ...prev.currentTransaction!,
                  status: 'error',
                  errorMessage: 'Amount too small (min $0.1)'
                }
              }));
              continue;
            }
            amountInUsd = proportionalAmount;
            break;
          case 'equal':
            const equalShare = investmentAmount / selectedTokens.length;
            if (equalShare < 0.1) {
              console.warn('Equal share amount too small:', {
                tokenSymbol: token.symbol,
                amount: equalShare
              });
              setCopyProgress(prev => ({
                ...prev,
                currentTransaction: {
                  ...prev.currentTransaction!,
                  status: 'error',
                  errorMessage: 'Amount too small (min $0.1)'
                }
              }));
              continue;
            }
            amountInUsd = equalShare;
            break;
          default:
            amountInUsd = token.usdValue;
        }

        const solInUsd = 1 / solPrice;
        const rawAmountInSol = amountInUsd * solInUsd;
        const amountInSol = Number(rawAmountInSol.toFixed(4));
        const roundedAmountInUsd = Number(amountInUsd.toFixed(4));

        if (roundedAmountInUsd < 0.1) {
          console.warn('Amount too small:', {
            tokenSymbol: token.symbol,
            amountInUsd: roundedAmountInUsd
          });
          setCopyProgress(prev => ({
            ...prev,
            currentTransaction: {
              ...prev.currentTransaction!,
              status: 'error',
              errorMessage: 'Amount too small (min $0.1)'
            }
          }));
          continue;
        }

        if (amountInSol > 0) {
          const selectedTokenMint = token.mint;

          console.log('Creating transaction with:', {
            tokenSymbol: token.symbol,
            selectedTokenMint,
            amountInSol,
            amountInUsd: roundedAmountInUsd
          });

          try {
            const { rawTransaction } = await createRawTransaction(
              publicKey.toString(),
              "So11111111111111111111111111111111111111112",
              selectedTokenMint,
              amountInSol
            );

            if (!rawTransaction) {
              throw new Error('Server error while creating transaction');
            }

            const binaryString = window.atob(rawTransaction);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            const transaction = VersionedTransaction.deserialize(bytes);
            transactions.push(transaction);
            totalAmount += roundedAmountInUsd;
            successfulTransactions++;

            setCopyProgress(prev => ({
              ...prev,
              currentTransaction: {
                ...prev.currentTransaction!,
                status: 'success'
              },
              details: prev.details.map(detail =>
                detail.text === `Preparing transaction for ${token.symbol}`
                  ? { ...detail, status: 'success' as const }
                  : detail
              ),
              successStats: {
                ...prev.successStats,
                successfulTransactions: prev.successStats.successfulTransactions + 1,
                totalAmount: prev.successStats.totalAmount + roundedAmountInUsd
              }
            }));
          } catch (error) {
            console.error(`Error creating transaction for ${token.symbol}:`, error);
            failedTransactions++;
            setCopyProgress(prev => ({
              ...prev,
              currentTransaction: {
                ...prev.currentTransaction!,
                status: 'error',
                errorMessage: error instanceof Error ? error.message : 'Failed to create transaction'
              },
              details: prev.details.map(detail =>
                detail.text === `Preparing transaction for ${token.symbol}`
                  ? {
                    ...detail,
                    status: 'error' as const,
                    errorMessage: error instanceof Error ? error.message : 'Failed to create transaction'
                  }
                  : detail
              ),
              successStats: {
                ...prev.successStats,
                failedTransactions: prev.successStats.failedTransactions + 1
              }
            }));
            continue;
          }
        }
      }

      if (transactions.length === 0) {
        throw new Error('No valid transactions to process');
      }

      setCopyProgress(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1,
        currentStatus: 'Waiting for transaction confirmation...',
        details: [
          ...prev.details,
          {
            text: '───────── Transaction Summary ─────────',
            status: 'pending' as const
          },
          {
            text: `Successfully prepared: ${prev.successStats.successfulTransactions} of ${selectedTokens.length} transactions ($${prev.successStats.totalAmount.toFixed(2)})`,
            status: 'success' as const
          },
          ...(prev.successStats.failedTransactions > 0 ? [{
            text: `Failed to prepare: ${selectedTokens
              .filter(token => prev.details.some(detail =>
                detail.text === `Preparing transaction for ${token.symbol}` &&
                detail.status === 'error'
              ))
              .map(t => t.symbol)
              .join(', ')}`,
            status: 'error' as const,
            errorMessage: 'These tokens will be skipped'
          }] : []),
          {
            text: 'Waiting for confirmation in Phantom wallet...',
            status: 'pending' as const
          }
        ],
        currentTransaction: null
      }));

      const backendResult = await sendTransactions(transactions);
      console.log('Backend transaction result:', backendResult);

      const finalSuccessCount = backendResult.SuccessTransactions || 0;
      const finalRejectedCount = backendResult.RejectedTransactions || 0;
      const finalTotalCount = backendResult.CountTransactions || 0;

      let statusMessage = '';
      let statusType: 'success' | 'warning' | 'error' = 'success';

      if (finalSuccessCount === finalTotalCount) {
        statusMessage = 'Portfolio copied successfully!';
        statusType = 'success';
      } else if (finalSuccessCount > 0) {
        statusMessage = 'Portfolio copied partially';
        statusType = 'warning';
      } else {
        statusMessage = 'Failed to copy portfolio';
        statusType = 'warning';
      }

      setCopyProgress(prev => ({
        ...prev,
        isCompleted: true,
        currentStatus: `${statusMessage}: ${finalSuccessCount} successful, ${finalRejectedCount} failed.`,
        statusMessage: statusMessage,
        statusType: statusType,
        successStats: {
          totalTokens: finalTotalCount,
          successfulTransactions: finalSuccessCount,
          failedTransactions: finalRejectedCount,
          totalAmount: totalAmount
        },
        details: [
          ...prev.details.filter(d => !d.text.includes('Waiting for confirmation') && !d.text.includes('Transaction Summary')),
          {
            text: '───────── Final Status ─────────',
            status: statusType
          },
          {
            text: `${statusMessage}: ${finalSuccessCount} successful, ${finalRejectedCount} failed (out of ${finalTotalCount} total).`,
            status: statusType
          },
          {
            text: 'You can now close this window or view your portfolio',
            status: 'success'
          }
        ]
      }));

      toast({
        title: statusMessage,
        description: `${finalSuccessCount} successful, ${finalRejectedCount} failed.`,
        status: statusType,
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error copying portfolio:', error);
      setCopyProgress(prev => ({
        ...prev,
        isError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to create or send transactions'
      }));
      message('Failed to create or send transactions', 'error');
    } finally {
      setIsCopying(false);
    }
  };

  const handleTryAgain = () => {
    setCopyProgress(prev => ({
      ...prev,
      isError: false,
      errorMessage: '',
      currentStep: 0,
      progress: 0,
      details: []
    }));
    setIsCopyModalOpen(false);
  };

  const handleViewPortfolio = () => {
    setIsCopyModalOpen(false);
    navigate(`/portfolio?address=${publicKey?.toString()}`);
  };

  const handleToggleToken = (mint: string) => {
    if (!wallet) return;

    if (mint === '__reset_filter__') {
      setWallet({
        ...wallet,
        tokens: wallet.tokens.map(token => ({
          ...token,
          selected: token.hasPool
        }))
      });
      return;
    }

    const token = wallet.tokens.find(t => t.mint === mint);

    if (!token) return;

    if (token.selected || token.hasPool) {
      setWallet({
        ...wallet,
        tokens: wallet.tokens.map(t =>
          t.mint === mint ? { ...t, selected: !t.selected } : t
        )
      });
      return;
    }


    setWarningModalState({
      isOpen: true,
      token,
      onConfirm: () => {
        setWallet({
          ...wallet,
          tokens: wallet.tokens.map(t =>
            t.mint === mint ? { ...t, selected: true } : t
          )
        });
        setWarningModalState(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const renderContent = () => {
    if (error) {
      return (
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          bg="#131313"
          border="1px solid rgba(60,60,60,0.47)"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="xl"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <div className={spacing({ mb: '4x' })} />
          <Typography variant='large_heading'>
            Error Loading Portfolio
          </Typography>
          <AlertDescription maxWidth="sm">
            {error}
          </AlertDescription>
          <div className={spacing({ mt: '4x' })} />
          <ButtonKit
            onClick={handleBackToHome}
          >
            Back to Home
          </ButtonKit>
        </Alert>
      );
    }

    return (
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <HStack spacing={4}>
            <Tooltip label="Go back">
              <IconButton
                aria-label="Back"
                icon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                variant="ghost"
                size="lg"
                colorScheme="blue"
                _hover={{ transform: 'translateX(-2px)' }}
              />
            </Tooltip>
            <Typography variant='3xLarge_heading'>Portfolio</Typography>
          </HStack>

          <HStack spacing={4}>
            <ButtonKit
              onClick={() => {

                let isInIframe = false;

                try {
                  isInIframe = window.self !== window.top;
                } catch (e) {
                  isInIframe = true; // Ошибка значит, мы в iframe с другого домена
                }

                if (isInIframe) {
                  const queryString = window.location.href.split('?')?.[1] || '';
                  const params = new URLSearchParams(queryString);
                  const address = params.get('address');
                  const url = document.referrer || 'https://pumpyagent.ai/';
                  // console.log(url);
                  navigator.clipboard.writeText(`${url}cloner/?address=${address}`);
                  message('Link copied to clipboard', 'success');
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  message('Link copied to clipboard', 'success');
                }
              }}
            >
              Share
            </ButtonKit>
            {!isMobile && <ButtonKit intent='brand' onClick={() => navigate('/')}>Back to Main Page</ButtonKit>}
          </HStack>
        </HStack>
        <WalletPortfolio
          address={wallet?.address || address || ''}
          balance={wallet?.balance || 0}
          tokens={wallet?.tokens || []}
          solBalance={solBalance}
          solUsdBalance={solUsdBalance}
          isLoading={isLoading}
          isCopying={isCopying}
          onCopyAddress={handleCopyAddress}
          onCopyPortfolio={handleCopyPortfolio}
          onToggleToken={handleToggleToken}
        />
      </VStack>
    );
  };

  return <div className={styles.page}>
    <div className={styles.heading}>
      <Header />
    </div>
    <Container maxW="container.lg">
      {renderContent()}
    </Container>
    <CopyProgressModal
      isOpen={isCopyModalOpen}
      onClose={() => setIsCopyModalOpen(false)}
      onCancel={() => setIsCopyModalOpen(false)}
      currentStep={copyProgress.currentStep}
      totalSteps={copyProgress.totalSteps}
      currentStatus={copyProgress.currentStatus}
      progress={(copyProgress.currentStep / copyProgress.totalSteps) * 100}
      details={copyProgress.details}
      isCompleted={copyProgress.isCompleted}
      isError={copyProgress.isError}
      errorMessage={copyProgress.errorMessage}
      successStats={copyProgress.successStats}
      onTryAgain={handleTryAgain}
      onViewPortfolio={handleViewPortfolio}
      currentTransaction={copyProgress.currentTransaction}
    />
    <WarningTokenModal
      isOpen={warningModalState.isOpen}
      onClose={() => setWarningModalState(prev => ({ ...prev, isOpen: false }))}
      onConfirm={warningModalState.onConfirm}
      onCancel={() => setWarningModalState(prev => ({ ...prev, isOpen: false }))}
      tokenSymbol={warningModalState.token?.symbol || ''}
      tokenIcon={warningModalState.token?.icon}
      reasons={[
        'No liquidity pool available',
        'This token might be a spam token',
        'Transaction might fail due to insufficient liquidity'
      ]}
    />

  </div>
}; 